import { create } from 'zustand'
import { getClient, createClient, destroyClient } from '../matrix/client'
import { bindClientToStores } from '../matrix/sync'

interface Session {
  userId: string
  accessToken: string
  homeserverUrl: string
  deviceId: string
}

interface AuthState {
  userId: string | null
  homeserverUrl: string | null
  isLoggedIn: boolean
  isLoggingIn: boolean
  isSyncing: boolean
  loginError: string | null

  login: (homeserver: string, user: string, password: string) => Promise<void>
  logout: () => Promise<void>
  restoreSession: () => Promise<boolean>
}

const STORAGE_KEY = 'chatpulse_session'

function saveSession(session: Session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

function loadSession(): Session | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Session
  } catch {
    return null
  }
}

function clearSession() {
  localStorage.removeItem(STORAGE_KEY)
}

export const useAuthStore = create<AuthState>((set) => ({
  userId: null,
  homeserverUrl: null,
  isLoggedIn: false,
  isLoggingIn: false,
  isSyncing: false,
  loginError: null,

  login: async (homeserver, user, password) => {
    set({ isLoggingIn: true, loginError: null })
    try {
      const baseUrl = homeserver.startsWith('http')
        ? homeserver
        : `https://${homeserver}`

      const tmpClient = await createClient({ baseUrl })
      const response = await tmpClient.loginWithPassword(user, password)

      const client = await createClient({
        baseUrl,
        accessToken: response.access_token,
        userId: response.user_id,
        deviceId: response.device_id,
      })

      saveSession({
        userId: response.user_id,
        accessToken: response.access_token,
        homeserverUrl: baseUrl,
        deviceId: response.device_id,
      })

      set({
        userId: response.user_id,
        homeserverUrl: baseUrl,
        isLoggedIn: true,
        isLoggingIn: false,
        isSyncing: true,
      })

      bindClientToStores(client)
      await client.startClient({ initialSyncLimit: 20 })
      set({ isSyncing: false })
    } catch (err) {
      set({
        isLoggingIn: false,
        loginError: err instanceof Error ? err.message : 'Login failed',
      })
    }
  },

  logout: async () => {
    try {
      const client = getClient()
      if (client) {
        client.stopClient()
        await client.logout(true)
      }
    } catch {
      // best effort
    }
    destroyClient()
    clearSession()
    set({
      userId: null,
      homeserverUrl: null,
      isLoggedIn: false,
      isSyncing: false,
    })
  },

  restoreSession: async () => {
    const session = loadSession()
    if (!session) return false

    try {
      const client = await createClient({
        baseUrl: session.homeserverUrl,
        accessToken: session.accessToken,
        userId: session.userId,
        deviceId: session.deviceId,
      })

      set({
        userId: session.userId,
        homeserverUrl: session.homeserverUrl,
        isLoggedIn: true,
        isSyncing: true,
      })

      bindClientToStores(client)
      await client.startClient({ initialSyncLimit: 20 })
      set({ isSyncing: false })
      return true
    } catch {
      clearSession()
      return false
    }
  },
}))
